import { HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { HttpHelperService } from "@services/http-helper.service";
import { Observable } from "rxjs";

import {
  AgentEInvoiceItem,
  AgentEInvoiceSearchParams,
  CancelPaymentPayload,
  CancelPaymentResponse,
  CancelSubmissionPayload,
  CancelSubmissionResponse,
  CopySubmissionRequest,
  CopySubmissionResponse,
  EpaymentAcknowledgePayload,
  EpaymentAcknowledgeResponse,
  ESubmissionAgentSearchParams,
  ESubmissionSearchParams,
  ESubmissionSearchResponse,
  OutstandingViewRequest,
  OutstandingViewResponse,
  PaymentConfigResponse,
  PaymentEnquirySearchParams,
  SaveDraftResponse,
  SubmissionEnquirySearchParams,
  SubmitPaymentPayload,
  SummaryPaymentRequest,
  SummarySearchRequest,
  SummarySearchResponse,
  TaxInvoiceStatementItem,
  TaxInvoiceStatementSearchParams,
  VerifySubmissionPayload,
  VerifySubmissionResponse,
} from "./e-submission.interface";

@Injectable({
  providedIn: "root",
})
export class ESubmissionService {
  constructor(private readonly httpHelperService: HttpHelperService) {}

  public getSubmissionsList(
    params: ESubmissionSearchParams,
  ): Observable<ESubmissionSearchResponse> {
    const httpParams = this.buildHttpParams({ ...params });
    return this.httpHelperService.getWithOptions(
      "/esubmission/enquiry",
      httpParams,
    );
  }

  private buildHttpParams(
    params: Record<string, string | number | boolean | null | undefined>,
  ): HttpParams {
    let httpParams = new HttpParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== "") {
        httpParams = httpParams.set(key, String(value));
      }
    });

    return httpParams;
  }

  /**
   * Get agents list for search modal
   */
  public getAgentsList(params: ESubmissionAgentSearchParams): Observable<any> {
    const httpParams = this.buildHttpParams({ ...params });
    return this.httpHelperService.getWithOptions(
      "/common/agent/searchlist",
      httpParams,
    );
  }

  /**
   * Get product type list for e-submission
   */
  public getProductTypes(userId: string): Observable<any> {
    const httpParams = this.buildHttpParams({ userId });
    return this.httpHelperService.getWithOptions(
      "/esubmission/product-types",
      httpParams,
    );
  }

  /**
   * Get search by options for e-submission
   */
  public getSearchByOptions(userId: string): Observable<any> {
    const httpParams = this.buildHttpParams({ userId });
    return this.httpHelperService.getWithOptions(
      "/esubmission/search-by",
      httpParams,
    );
  }

  public getAgentEInvoiceTypeOptions(userId: string): Observable<any> {
    const httpParams = this.buildHttpParams({ userId });
    return this.httpHelperService.getWithOptions(
      "/esubmission/agent-e-invoice-types",
      httpParams,
    );
  }

  public getAgentEInvoiceSearchByOptions(userId: string): Observable<any> {
    const httpParams = this.buildHttpParams({ userId });
    return this.httpHelperService.getWithOptions(
      "/esubmission/agent-e-invoice-search-by",
      httpParams,
    );
  }

  public getEnquirySearchByOptions(userId: string): Observable<any> {
    const httpParams = this.buildHttpParams({ userId });
    return this.httpHelperService.getWithOptions(
      "/esubmission/search-by-enquiry",
      httpParams,
    );
  }

  /**
   * Get CBC outstanding days options for e-submission
   */
  public getCbcOutstandingDaysOptions(userId: string): Observable<any> {
    const httpParams = this.buildHttpParams({ userId });
    return this.httpHelperService.getWithOptions(
      "/esubmission/cbc-outstanding-days",
      httpParams,
    );
  }

  public getSubmitStatusOptions(userId: string): Observable<any> {
    const httpParams = this.buildHttpParams({ userId });
    return this.httpHelperService.getWithOptions(
      "/esubmission/submit-status",
      httpParams,
    );
  }

  public getPaymentStatusOptions(userId: string): Observable<any> {
    const httpParams = this.buildHttpParams({ userId });
    return this.httpHelperService.getWithOptions(
      "/esubmission/payment-status",
      httpParams,
    );
  }

  public getPaymentEnquirySearchByOptions(userId: string): Observable<any> {
    const httpParams = this.buildHttpParams({ userId });
    return this.httpHelperService.getWithOptions(
      "/esubmission/search-by-payment-enquiry",
      httpParams,
    );
  }

  public getDocumentTypeOptions(userId: string): Observable<any> {
    const httpParams = this.buildHttpParams({ userId });
    return this.httpHelperService.getWithOptions(
      "/esubmission/document-type",
      httpParams,
    );
  }

  public getPaymentModeOptions(userId: string): Observable<any> {
    const httpParams = this.buildHttpParams({ userId });
    return this.httpHelperService.getWithOptions(
      "/esubmission/payment-mode",
      httpParams,
    );
  }

  public getBankNameOptions(userId: string): Observable<any> {
    const httpParams = this.buildHttpParams({ userId });
    return this.httpHelperService.getWithOptions(
      "/esubmission/bank-names",
      httpParams,
    );
  }

  public getSubmissionEnquiryList(
    params: SubmissionEnquirySearchParams,
  ): Observable<any> {
    const httpParams = this.buildHttpParams({ ...params });
    return this.httpHelperService.getWithOptions(
      "/esubmission/enquiry-list",
      httpParams,
    );
  }

  public getPaymentEnquiryList(
    params: PaymentEnquirySearchParams,
  ): Observable<any> {
    const httpParams = this.buildHttpParams({ ...params });
    return this.httpHelperService.getWithOptions(
      "/esubmission/payment-enquiry-list",
      httpParams,
    );
  }

  public getSubmissionDetail(submissionNo: string): Observable<any> {
    const httpParams = this.buildHttpParams({ submissionNo });
    return this.httpHelperService.getWithOptions(
      "/esubmission/submission-detail",
      httpParams,
    );
  }

  public getOutstandingView(
    params: OutstandingViewRequest,
  ): Observable<OutstandingViewResponse> {
    const httpParams = this.buildHttpParams({ ...params });
    return this.httpHelperService.getWithOptions(
      "/esubmission/outstanding-view",
      httpParams,
    );
  }

  public getSummarySearch(
    params: SummarySearchRequest,
  ): Observable<SummarySearchResponse> {
    const httpParams = this.buildHttpParams({ ...params });
    return this.httpHelperService.getWithOptions(
      "/esubmission/summary-search",
      httpParams,
    );
  }

  public getSummaryPaymentItems(
    payload: SummaryPaymentRequest,
  ): Observable<ESubmissionSearchResponse> {
    return this.httpHelperService.postWithOptions(
      "/esubmission/summary-payment",
      payload,
    );
  }

  public submitPayment(payload: SubmitPaymentPayload): Observable<any> {
    return this.httpHelperService.postWithOptions(
      "/esubmission/submit-payment",
      payload,
    );
  }

  public getAgentEInvoiceList(
    params: AgentEInvoiceSearchParams,
  ): Observable<{ data: AgentEInvoiceItem[]; totalRecords: number }> {
    const httpParams = this.buildHttpParams({ ...params });
    return this.httpHelperService.getWithOptions(
      "/esubmission/agent-e-invoice-list",
      httpParams,
    );
  }

  public getTaxInvoiceStatementList(
    params: TaxInvoiceStatementSearchParams,
  ): Observable<{ data: TaxInvoiceStatementItem[]; totalRecords: number }> {
    const httpParams = this.buildHttpParams({ ...params });
    return this.httpHelperService.getWithOptions(
      "/esubmission/tax-invoice-statement-listing",
      httpParams,
    );
  }

  // ===== Write-side methods =====

  public verifySubmission(
    payload: VerifySubmissionPayload,
  ): Observable<VerifySubmissionResponse> {
    return this.httpHelperService.postWithOptions(
      "/esubmission/submission-payment-verify",
      payload,
    );
  }

  public saveDraft(
    payload: SubmitPaymentPayload,
  ): Observable<SaveDraftResponse> {
    return this.httpHelperService.postWithOptions(
      "/esubmission/save-draft",
      payload,
    );
  }

  public cancelSubmission(
    payload: CancelSubmissionPayload,
  ): Observable<CancelSubmissionResponse> {
    return this.httpHelperService.postWithOptions(
      "/esubmission/submission-cancel",
      payload,
    );
  }

  public cancelPayment(
    payload: CancelPaymentPayload,
  ): Observable<CancelPaymentResponse> {
    return this.httpHelperService.postWithOptions(
      "/esubmission/payment-cancel",
      payload,
    );
  }

  public copySubmission(
    payload: CopySubmissionRequest,
  ): Observable<CopySubmissionResponse> {
    return this.httpHelperService.postWithOptions(
      "/esubmission/copy-submission",
      payload,
    );
  }

  public acknowledgeEpayment(
    payload: EpaymentAcknowledgePayload,
  ): Observable<EpaymentAcknowledgeResponse> {
    return this.httpHelperService.postWithOptions(
      "/esubmission/epayment-acknowledge",
      payload,
    );
  }

  // ===== Submission Payment =====

  public getPaymentConfig(): Observable<PaymentConfigResponse> {
    return this.httpHelperService.getWithOptions(
      "/esubmission/payment-config",
      this.buildHttpParams({}),
    );
  }
}
